import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL =
  "https://instatenders-supplier-upload.s3.ap-south-1.amazonaws.com";

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true ||
    success === "true" ||
    success === 1 ||
    success === "1"
  );
}

function pickMaterialFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (dataBlock.material && typeof dataBlock.material === "object") {
    return dataBlock.material;
  }

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.materials == null &&
    (dataBlock._id != null ||
      dataBlock.id != null ||
      dataBlock.materialCode != null ||
      dataBlock.productName != null)
  ) {
    return dataBlock;
  }

  const list = dataBlock.materials;
  if (!Array.isArray(list) || list.length === 0) return null;
  const found = list.find((m) => String(m._id || m.id) === String(id));
  return found || list[0];
}

function buildS3Url(s3Key) {
  const key = String(s3Key || "").trim().replace(/^\/+/, "");
  if (!key) return "";
  if (/^https?:\/\//i.test(key)) return key;
  return `${S3_BASE_URL}/${key}`;
}

function normalizeMaterialImages(material) {
  if (!material || typeof material !== "object") return [];

  if (Array.isArray(material.images) && material.images.length > 0) {
    return material.images
      .map((item, index) => {
        if (typeof item === "string") {
          const url = buildS3Url(item);
          return url ? { url, originalFileName: `Image ${index + 1}` } : null;
        }
        if (!item || typeof item !== "object") return null;
        const url =
          item.url ||
          item.imageUrl ||
          item.publicUrl ||
          buildS3Url(item.s3Key || item.key);
        if (!url) return null;
        return {
          url,
          originalFileName:
            item.originalFileName || item.fileName || `Image ${index + 1}`,
        };
      })
      .filter(Boolean);
  }

  const s3Keys = Array.isArray(material.s3Keys) ? material.s3Keys : [];
  const originalFileNames = Array.isArray(material.originalFileNames)
    ? material.originalFileNames
    : [];

  return s3Keys
    .map((s3Key, index) => {
      const url = buildS3Url(s3Key);
      if (!url) return null;
      return {
        url,
        originalFileName:
          originalFileNames[index] || `Image ${index + 1}`,
      };
    })
    .filter(Boolean);
}

function normalizeMaterialAttachments(material) {
  if (!material || typeof material !== "object") return [];
  if (!Array.isArray(material.attachments) || material.attachments.length === 0) {
    return [];
  }

  return material.attachments
    .map((item, index) => {
      if (typeof item === "string") {
        const url = buildS3Url(item);
        return url
          ? {
              url,
              s3Key: item,
              originalFileName: `Attachment ${index + 1}`,
              contentType: "",
            }
          : null;
      }
      if (!item || typeof item !== "object") return null;
      const url =
        item.url ||
        item.downloadUrl ||
        item.publicUrl ||
        buildS3Url(item.s3Key || item.key);
      if (!url) return null;
      return {
        url,
        s3Key: item.s3Key || item.key || "",
        originalFileName:
          item.originalFileName ||
          item.fileName ||
          item.name ||
          `Attachment ${index + 1}`,
        contentType: item.contentType || "",
        uploadedAt: item.uploadedAt || "",
      };
    })
    .filter(Boolean);
}

function isImageAttachment(attachment) {
  const type = String(attachment?.contentType || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  const name = String(attachment?.originalFileName || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
}

function attachmentIconClass(attachment) {
  const type = String(attachment?.contentType || "").toLowerCase();
  const name = String(attachment?.originalFileName || "").toLowerCase();
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) {
    return "fa-solid fa-image";
  }
  if (type.includes("pdf") || name.endsWith(".pdf")) {
    return "fa-solid fa-file-pdf";
  }
  if (
    type.includes("sheet") ||
    type.includes("excel") ||
    /\.(xlsx?|csv)$/i.test(name)
  ) {
    return "fa-solid fa-file-excel";
  }
  if (type.includes("word") || /\.(docx?)$/i.test(name)) {
    return "fa-solid fa-file-word";
  }
  if (type.startsWith("text/") || name.endsWith(".txt")) {
    return "fa-solid fa-file-lines";
  }
  return "fa-solid fa-paperclip";
}

function MaterialViewAttachments({ attachments = [] }) {
  if (!attachments.length) {
    return <div className="txtdsply text-muted">No attachments</div>;
  }

  return (
    <ul className="list-group">
      {attachments.map((att, index) => (
        <li
          key={`${att.s3Key || att.url}-${index}`}
          className="list-group-item d-flex justify-content-between align-items-center py-2"
        >
          <div className="d-flex align-items-center text-truncate mr-2">
            <i className={`${attachmentIconClass(att)} mr-2`} />
            <div className="text-truncate">
              <div className="text-truncate" title={att.originalFileName}>
                {att.originalFileName}
              </div>
              {att.uploadedAt ? (
                <small className="text-muted">
                  {formatDateToDisplay(att.uploadedAt)}
                </small>
              ) : null}
            </div>
          </div>
          <a
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-secondary"
            title={isImageAttachment(att) ? "Open" : "Download / Open"}
            download={att.originalFileName || undefined}
          >
            <i
              className={
                isImageAttachment(att)
                  ? "fa-solid fa-eye"
                  : "fa-solid fa-download"
              }
            />
          </a>
        </li>
      ))}
    </ul>
  );
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function displayUser(ref) {
  if (!ref) return "-";
  if (typeof ref === "string") return ref.trim() || "-";
  return ref.name || ref.userName || ref.email || "-";
}

function categoryLabel(material) {
  const id =
    material?.categoryId ||
    material?.category?.categoryId ||
    "";
  const name =
    material?.categoryName ||
    material?.category?.categoryName ||
    "";
  if (id && name) return `${id} - ${name}`;
  return name || id || "-";
}

function subcategoryLabel(material) {
  const id =
    material?.subcategoryId ||
    material?.subcategory?.subcategoryId ||
    "";
  const name =
    material?.subcategoryName ||
    material?.subcategory?.subcategoryName ||
    "";
  if (id && name) return `${id} - ${name}`;
  return name || id || "-";
}

function formatDateToDisplay(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return displayValue(dateString);
  return date.toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
  });
}

function renderStatusBadge(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  const normalized = value.toLowerCase();
  const badgeClass =
    normalized === "active"
      ? "badge-success"
      : normalized === "inactive"
        ? "badge-danger"
        : "badge-secondary";
  return (
    <span
      className={`badge ${badgeClass}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {value}
    </span>
  );
}

function MaterialViewImages({ images = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    setCurrentIndex(0);
    setImageErrors({});
  }, [images]);

  if (!images.length) {
    return <div className="txtdsply text-muted">No images</div>;
  }

  const current = images[currentIndex];
  const hasMultiple = images.length > 1;
  const showImage = current?.url && !imageErrors[currentIndex];

  return (
    <div>
      <div
        style={{
          width: "100%",
          maxWidth: "320px",
          minHeight: "200px",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #dee2e6",
          backgroundColor: "#f8f9fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {showImage ? (
          <img
            src={current.url}
            alt={current.originalFileName || "Material image"}
            onError={() =>
              setImageErrors((prev) => ({ ...prev, [currentIndex]: true }))
            }
            style={{
              maxWidth: "100%",
              maxHeight: "280px",
              objectFit: "contain",
            }}
          />
        ) : (
          <div className="text-muted py-4">Image unavailable</div>
        )}

        {hasMultiple && showImage && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-dark position-absolute"
              style={{
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                opacity: 0.8,
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                padding: 0,
              }}
              onClick={() =>
                setCurrentIndex((prev) =>
                  prev === 0 ? images.length - 1 : prev - 1,
                )
              }
              title="Previous image"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-dark position-absolute"
              style={{
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                opacity: 0.8,
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                padding: 0,
              }}
              onClick={() =>
                setCurrentIndex((prev) =>
                  prev === images.length - 1 ? 0 : prev + 1,
                )
              }
              title="Next image"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
            <div
              className="position-absolute bg-dark text-white px-2 py-1"
              style={{
                bottom: "8px",
                right: "8px",
                fontSize: "11px",
                borderRadius: "12px",
                opacity: 0.85,
              }}
            >
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="d-flex flex-wrap mt-3" style={{ gap: "10px" }}>
          {images.map((image, index) => (
            <button
              key={`${image.url}-${index}`}
              type="button"
              onClick={() => setCurrentIndex(index)}
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "8px",
                overflow: "hidden",
                border:
                  index === currentIndex
                    ? "2px solid #1199B2"
                    : "1px solid #dee2e6",
                padding: 0,
                background: "#fff",
              }}
              title={image.originalFileName}
            >
              {!imageErrors[index] ? (
                <img
                  src={image.url}
                  alt={image.originalFileName || `Thumbnail ${index + 1}`}
                  onError={() =>
                    setImageErrors((prev) => ({ ...prev, [index]: true }))
                  }
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span className="text-muted" style={{ fontSize: "10px" }}>
                  N/A
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ViewField({ label, children }) {
  return (
    <div className="form-group row align-items-center">
      <label className="col-12 col-md-4 col-form-label">{label}</label>
      <div className="col-12 col-md-8">
        <div className="txtdsply">{children}</div>
      </div>
    </div>
  );
}

function MaterialViewShell({ title, children }) {
  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">{title}</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/material">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MaterialView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const images = useMemo(
    () => normalizeMaterialImages(material),
    [material],
  );

  const attachments = useMemo(
    () => normalizeMaterialAttachments(material),
    [material],
  );

  const materialId = material?._id || material?.id || id;

  const fetchMaterial = async () => {
    if (!id) {
      toast.error("Material ID is missing", { autoClose: 2000 });
      navigate("/material");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-material-by-id",
        id,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        const row = pickMaterialFromResponse(responseData, id);
        if (row) {
          setMaterial(row);
        } else {
          toast.error("Material not found", { autoClose: 2000 });
          navigate("/material");
        }
      } else {
        toast.error(
          responseData?.message || "Failed to fetch material details",
          { autoClose: 2000 },
        );
        navigate("/material");
      }
    } catch (error) {
      console.error("Fetch material error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while fetching material details",
        { autoClose: 2000 },
      );
      navigate("/material");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) {
    return (
      <MaterialViewShell title="View Material">
        <div className="card-body text-center py-5">
          <PulseLoader color="#1199B2" size={10} />
        </div>
      </MaterialViewShell>
    );
  }

  if (!material) {
    return (
      <MaterialViewShell title="View Material">
        <div className="card-body text-center py-5">
          <p>Material not found</p>
        </div>
      </MaterialViewShell>
    );
  }

  return (
    <MaterialViewShell title="View Material">
      <form className="form-horizontal">
        <div className="card-body">
          <ViewField label="Material Code">
            {displayValue(
              material.materialCode || material.materialId || material.code,
            )}
          </ViewField>

          <ViewField label="Category">{categoryLabel(material)}</ViewField>

          <ViewField label="Subcategory">
            {subcategoryLabel(material)}
          </ViewField>

          <ViewField label="Product Name">
            {displayValue(
              material.productName ||
                material.materialName ||
                material.name,
            )}
          </ViewField>

          <ViewField label="Part Number">
             {displayValue(material.partNumber)}
          </ViewField>

          <ViewField label="Make / Brand / OEM">
          {displayValue( material.makeBrandOem )}
          </ViewField>

          <ViewField label="HSN Code">
            {displayValue(material.hsnCode || material.hsn)}
          </ViewField>

          <ViewField label="UOM">
            {displayValue(material.uom || material.unit)}
          </ViewField>

           <ViewField label="Material Description">
            {displayValue(material.materialDescription)}
          </ViewField>

          <ViewField label="Stock Category">
            {displayValue(
              material.stockCategory || material.stockCategoryName,
            )}
          </ViewField>

          <ViewField label="Status">
            {renderStatusBadge(material.status)}
          </ViewField>

          <div className="form-group row align-items-start">
            <label className="col-12 col-md-4 col-form-label">Images</label>
            <div className="col-12 col-md-8">
              <MaterialViewImages images={images} />
            </div>
          </div>

          <div className="form-group row align-items-start">
            <label className="col-12 col-md-4 col-form-label">Attachments</label>
            <div className="col-12 col-md-8">
              <MaterialViewAttachments attachments={attachments} />
            </div>
          </div>

          <ViewField label="Created By">
            {displayUser(material.createdBy || material.createdById)}
          </ViewField>

          <ViewField label="Created At">
            {formatDateToDisplay(material.createdAt)}
          </ViewField>

          <ViewField label="Updated By">
            {displayUser(material.updatedBy || material.updatedById)}
          </ViewField>

          <ViewField label="Updated At">
            {formatDateToDisplay(material.updatedAt)}
          </ViewField>
        </div>

        <div className="card-footer text-center">
          <Link to={`/materialEdit/${materialId}`} className="btn btn-blue mr-2">
            Edit
          </Link>
        </div>
      </form>
    </MaterialViewShell>
  );
};

export default MaterialView;
